import { FC, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import {
  Box,
  CircularProgress,
  InputAdornment,
  Skeleton,
  Typography,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SendIcon from '@mui/icons-material/Send';
import GroupIcon from '@mui/icons-material/Group';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import { CustomTextfield } from '@/components/CustomTextfield/styles';
import { errorReporter } from '@/services/errorReportingService';
import useAlertStore from '@/stores/alertStore';
import { ButtonsGrid, HeaderGridContainer, NoteHeaderTitle, TextGrid } from './styles';
import DeleteModal from '@/components/CustomModal/DeleteModal';
import { RouteMap } from '@/routes/Routes';
import { useUpdateNote, useDeleteNote, useToggleFavorite } from '@/services/noteGoApi/noteGoApiComponents';
import { queryKeyFn } from '@/services/noteGoApi/noteGoApiContext';
import { useAuthStore } from '@/stores/authStore';
import InviteModal from '@/components/CustomModal/InviteModal';
import { NoteOut } from '@/services/noteGoApi/noteGoApiSchemas';
import { ShimmerButton } from '@/components/ShimmerButton';
import { useOrganizations } from '@/features/betaOrganizations/hooks/useOrganizations';
import TeamShareFolderModal from '@/features/betaOrganizations/components/TeamShareFolderModal';
import ThumbnailPickerModal from '@/features/thumbnailPicker/components/ThumbnailPickerModal';
import { THUMBNAIL_PICKER_FEATURE_ENABLED } from '@/features/thumbnailPicker/config';
import { getDeleteNoteErrorMessage } from '@/utils/deleteNoteError';
import { DEFAULT_TITLE_PLACEHOLDER } from '../constants';

// Keyframe animation for the share arrow
const bounceAnimation = {
  '@keyframes bounceRight': {
    '0%, 100%': {
      transform: 'translateX(0)',
    },
    '50%': {
      transform: 'translateX(8px)',
    },
  },
};

interface NoteHeaderProps {
  title: string;
  date: string;
  isOwner: boolean;
  isProcessing?: boolean;
  processingTitleVisible?: boolean;
  isLoadingNote?: boolean;
  sharedBy?: {
    name: string;
    email?: string;
  } | null;
  onTitleUpdate?: (newTitle: string) => void;
  note?: NoteOut | null;
  shouldShowShareArrow?: boolean;
  onToggleSave?: () => void;
  isSaved?: boolean;
  isSaveMutating?: boolean;
  showZapierButton?: boolean;
  onSendToZapier?: () => void;
  isSendingToZapier?: boolean;
}

const NoteHeader: FC<NoteHeaderProps> = ({
  title,
  date,
  isOwner,
  isProcessing,
  processingTitleVisible = true,
  isLoadingNote,
  sharedBy,
  onTitleUpdate,
  note,
  shouldShowShareArrow = false,
  onToggleSave,
  isSaved = false,
  isSaveMutating = false,
  showZapierButton,
  onSendToZapier,
  isSendingToZapier,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // 600px and below - true mobile
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { noteId } = useParams();
  const [editedTitle, setEditedTitle] = useState(title || '');
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const showAlert = useAlertStore((state) => state.showAlert);
  const inpRef = useRef<HTMLInputElement>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isTeamShareModalOpen, setIsTeamShareModalOpen] = useState(false);
  const [isThumbnailModalOpen, setIsThumbnailModalOpen] = useState(false);
  const { mutateAsync: updateNote } = useUpdateNote();
  const { mutateAsync: deleteNote } = useDeleteNote();
  const { mutateAsync: toggleFavorite } = useToggleFavorite();
  const { userToken, userData } = useAuthStore();
  const { organizations } = useOrganizations();
  const team = organizations.find((org) => org.owner_id === userData?.firebase_uid) ?? organizations[0];
  const isSharedWithTeam = !!(team && noteId && team.note_ids.includes(noteId));

  const handleUpdateTitle = async () => {
    // Compare against the raw persisted title (not display title with placeholder)
    const rawTitle = note?.title ?? '';
    if (isEditing && editedTitle !== rawTitle && noteId && !isUpdating) {
      setIsUpdating(true);
      try {
        const payload = {
          headers: { authorization: `Bearer ${userToken}` },
          pathParams: { noteId },
          body: { title: editedTitle },
        };

        await updateNote(payload, {
          onSuccess: () => {
            onTitleUpdate?.(editedTitle);

            // Optimistically update the cache immediately with the new title
            if (noteId && userToken) {
              const queryKey = queryKeyFn({
                path: '/notes/{noteId}',
                operationId: 'getNote',
                variables: {
                  pathParams: { noteId },
                  headers: { authorization: `Bearer ${userToken}` },
                },
              });

              // Update the cache immediately with the new title
              queryClient.setQueryData(queryKey, (oldData: NoteOut | undefined) => {
                if (oldData) {
                  return { ...oldData, title: editedTitle };
                }
                return oldData;
              });
            }
            setIsEditing(false); // Only exit edit mode after a successful update
          },
          onError: () => {
            showAlert('Something went wrong while updating the title. Try again later.', 'error');
            // Stay in edit mode
          },
        });
      } catch {
        // mutateAsync always rejects on error even with onError configured —
        // the alert above already handles it; swallow to avoid an unhandled rejection
      } finally {
        setIsUpdating(false);
      }
      return;
    }
    setIsEditing(false); // Always exit edit mode for no-change or invalid cases
  };

  const handleTitleClick = () => {
    if (!isOwner || isProcessing || isUpdating) return;
    // Clear the default placeholder text when entering edit mode, but only if the note has no real title
    if (!note?.title && editedTitle === DEFAULT_TITLE_PLACEHOLDER) {
      setEditedTitle('');
    }
    setIsEditing(true);
    if (inpRef.current) {
      inpRef.current.focus();
    }
  };

  const handleOpenDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (noteId) {
      try {
        await deleteNote(
          {
            headers: { authorization: `Bearer ${userToken}` },
            pathParams: { noteId },
          },
          {
            onSuccess: () => {
              navigate(RouteMap.MY_NOTES);
              showAlert('Note deleted successfully.', 'success');
            },
            onError: (error) => {
              showAlert(getDeleteNoteErrorMessage(error), 'error');
            },
          },
        );
      } catch {
        // mutateAsync always rejects on error even with onError configured —
        // the alert above already handles it; swallow to avoid an unhandled rejection
      }
    }
    setIsDeleteModalOpen(false);
  };

  const handleOpenInviteModal = () => {
    setIsInviteModalOpen(true);
  };

  const handleToggleFavorite = async () => {
    if (!noteId || !note) return;

    try {
      await toggleFavorite(
        {
          headers: { authorization: `Bearer ${userToken}` },
          pathParams: { noteId },
          body: { is_favorite: !note.is_favorite },
        },
        {
          onSuccess: () => {
            // Invalidate the note query to get fresh data
            if (noteId && userToken) {
              const queryKey = queryKeyFn({
                path: '/notes/{noteId}',
                operationId: 'getNote',
                variables: {
                  pathParams: { noteId },
                  headers: { authorization: `Bearer ${userToken}` },
                },
              });
              queryClient.invalidateQueries({ queryKey });
            }
          },
          onError: () => {
            showAlert('Something went wrong while updating favorite status. Try again later.', 'error');
          },
        },
      );
    } catch (err) {
      errorReporter.expected('Failed to toggle favorite status', err, {
        feature: 'notes',
        operation: 'toggleFavorite',
        noteId,
      });
      showAlert('Something went wrong while updating favorite status. Try again later.', 'error');
    }
  };

  useEffect(() => {
    // Sync unconditionally. Guarding on `if (title)` meant an empty/untitled note
    // kept the PREVIOUS note's title in the edit field after SPA navigation.
    setEditedTitle(title ?? '');
  }, [title]);

  return (
    <>
      {/* NOTE: no <Helmet> here. NoteLayout (this component's parent) already
          renders the note's meta tags using the AI summary as the description.
          A second Helmet in this deeper child WINS, which is how og:description
          ended up being the note's date string in link previews. */}
      <HeaderGridContainer container spacing={isMobile ? 2 : 4}>
        {!isLoadingNote && (
          <TextGrid size={isMobile ? 12 : 8}>
            {isEditing && isOwner && !isProcessing && !isUpdating ? (
              <CustomTextfield
                inputRef={inpRef}
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                focused
                onBlur={handleUpdateTitle}
                onKeyDown={(e) => e.key === 'Enter' && handleUpdateTitle()}
                onFocus={() => setIsEditing(true)}
                variant="outlined"
                size="small"
                fullWidth
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Edit />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            ) : (
              <>
                <NoteHeaderTitle
                  variant="h1"
                  onClick={handleTitleClick}
                  isOwner={isOwner && !isProcessing && !isUpdating}
                  sx={{
                    opacity: isProcessing ? (processingTitleVisible ? 1 : 0) : 1,
                    transition: 'opacity 0.3s ease-in-out',
                    color: isProcessing ? 'text.secondary' : 'text.primary',
                  }}
                >
                  {editedTitle}
                </NoteHeaderTitle>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    gap: isMobile ? 0.5 : 1,
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1">{date}</Typography>
                    {note && isOwner && (
                      <>
                        <Tooltip title={note.is_favorite ? 'Remove from favorites' : 'Add to favorites'}>
                          <IconButton
                            onClick={handleToggleFavorite}
                            size="small"
                            aria-label={note.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                            sx={{ ml: 1 }}
                          >
                            {note.is_favorite ? <StarIcon sx={{ color: '#FFD700' }} /> : <StarBorderIcon />}
                          </IconButton>
                        </Tooltip>
                        {showZapierButton && (
                          <Tooltip title="Send to Zapier">
                            <span>
                              <IconButton
                                onClick={onSendToZapier}
                                disabled={isSendingToZapier}
                                size="small"
                                aria-label="Send to Zapier"
                              >
                                {isSendingToZapier ? <CircularProgress size={20} /> : <SendIcon />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {THUMBNAIL_PICKER_FEATURE_ENABLED && note.video_url && note.thumbnail_url && !isProcessing && (
                          <Tooltip title="Choose thumbnail">
                            <IconButton
                              onClick={() => setIsThumbnailModalOpen(true)}
                              size="small"
                              aria-label="Choose thumbnail"
                            >
                              <PhotoLibraryOutlinedIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {team && (
                          <Tooltip title="Move note">
                            <IconButton
                              onClick={() => setIsTeamShareModalOpen(true)}
                              size="small"
                              aria-label="Move note"
                            >
                              {isSharedWithTeam ? <GroupIcon sx={{ color: 'primary.main' }} /> : <GroupOutlinedIcon />}
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete note">
                          <IconButton
                            onClick={handleOpenDeleteModal}
                            size="small"
                            aria-label="Delete note"
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                  {/* Only show "Shared by" when the note belongs to someone else */}
                  {!isOwner && sharedBy && (
                    <Typography
                      variant="body1"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: 'primary.light',
                        color: 'primary.contrastText',
                        px: isMobile ? 0.75 : 1,
                        py: isMobile ? 0.25 : 0.5,
                        borderRadius: 1,
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                        ml: isMobile ? 0 : 1,
                        mt: isMobile ? 0.5 : 0,
                      }}
                    >
                      Shared by: {sharedBy.name}
                    </Typography>
                  )}
                  {onToggleSave && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        ml: isMobile ? 0 : 1,
                        mt: isMobile ? 0.5 : 0,
                      }}
                    >
                      <Tooltip title={isSaved ? 'Remove from Watched Notes' : 'Save to Watched Notes'}>
                        <IconButton
                          onClick={onToggleSave}
                          disabled={isSaveMutating}
                          size="small"
                          aria-label={isSaved ? 'Remove from Watched Notes' : 'Save to Watched Notes'}
                          sx={{ color: isSaved ? 'primary.main' : 'text.secondary' }}
                        >
                          {isSaved ? <BookmarkIcon /> : <BookmarkBorderOutlinedIcon />}
                        </IconButton>
                      </Tooltip>
                      <Typography variant="body2" color="text.secondary">
                        {isSaved ? 'Saved' : 'Save to Watched Notes'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </TextGrid>
        )}
        {isLoadingNote && (
          <TextGrid size={isMobile ? 12 : 8}>
            <Skeleton variant="text" height={70} sx={{ maxWidth: '800px', width: '100%' }} />
          </TextGrid>
        )}

        {isOwner && (
          <ButtonsGrid
            size={isMobile ? 12 : 4}
            sx={{
              justifyContent: isMobile ? 'flex-start' : 'flex-end',
              mt: isMobile ? 2 : 0,
            }}
          >
            <Box position="relative" display="inline-flex">
              {/* Tooltip bubble below Share button - only shows during processing */}
              {!isMobile && isProcessing && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    zIndex: theme.zIndex.appBar,
                    background: '#d1e7dd',
                    color: '#000000',
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    fontSize: '0.9rem',
                    //fontWeight: 500,
                    whiteSpace: 'nowrap',
                    border: '2px solid #badbcc',
                    //boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)',
                    pointerEvents: 'none',
                    // Triangle pointer pointing up - border layer
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      bottom: '100%',
                      right: '22px',
                      border: '10px solid transparent',
                      borderBottomColor: '#badbcc',
                    },
                    // Triangle pointer pointing up - fill layer
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: '100%',
                      right: '24px',
                      border: '8px solid transparent',
                      borderBottomColor: '#d1e7dd',
                    },
                  }}
                >
                  You can now share this
                </Box>
              )}
              <ShimmerButton
                variant="contained"
                color="primary"
                size="large"
                onClick={handleOpenInviteModal}
                shimmerOpacity={0.3}
              >
                Share
              </ShimmerButton>
              {/* Animated elegant arrow pointing to Share button for trial users */}
              {!isMobile && shouldShowShareArrow && (
                <Box
                  sx={{
                    ...bounceAnimation,
                    position: 'absolute',
                    right: 'calc(100% + 12px)',
                    top: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    animation: 'bounceRight 1.5s ease-in-out infinite',
                    pointerEvents: 'none',
                  }}
                >
                  {/* Modern curved arrow SVG */}
                  <svg
                    width="40"
                    height="20"
                    viewBox="0 0 40 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ display: 'block' }}
                  >
                    {/* Arrow tail - longer horizontal line */}
                    <path d="M2 10H30" stroke="#9E9E9E" strokeWidth="2.5" strokeLinecap="round" />
                    {/* Sleek arrowhead */}
                    <path
                      d="M28 4L36 10L28 16"
                      stroke="#9E9E9E"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Box>
              )}
            </Box>
          </ButtonsGrid>
        )}
      </HeaderGridContainer>
      <DeleteModal
        open={isDeleteModalOpen}
        handleClose={() => setIsDeleteModalOpen(false)}
        text="Are you sure you want to delete this note? This action cannot be undone."
        confirmButtonText="Yes, delete"
        onConfirm={handleConfirmDelete}
      />
      <InviteModal open={isInviteModalOpen} handleClose={() => setIsInviteModalOpen(false)} />
      {THUMBNAIL_PICKER_FEATURE_ENABLED && isOwner && noteId && (
        <ThumbnailPickerModal
          open={isThumbnailModalOpen}
          onClose={() => setIsThumbnailModalOpen(false)}
          noteId={noteId}
        />
      )}
      {team && noteId && (
        <TeamShareFolderModal
          open={isTeamShareModalOpen}
          onClose={() => setIsTeamShareModalOpen(false)}
          organization={team}
          noteId={noteId}
        />
      )}
    </>
  );
};

export default NoteHeader;
